import { EventEmitter } from '../core/Event';
import type { Theme } from './Theme';

export interface ThemeChangedEvent {
  kind: 'theme-changed';
  before: string;
  after: string;
  theme: Theme;
}

/**
 * In-memory registry of every {@link Theme} the engine knows about, plus
 * the currently-active theme. Scenes read {@link current} at build time and
 * subscribe to {@link events} to react to runtime swaps without rebuilding
 * the whole scene.
 *
 * <p>A registry is constructed once per host (typically once per Pixi app)
 * and shared across scenes. Tests can construct a fresh registry per case.
 *
 * <p>Phase C.1 ships local themes only; Phase C.2 adds a {@code loadFromUrl}
 * path that fetches a theme bundle from CDN at runtime.
 */
export class ThemeRegistry {
  public readonly events = new EventEmitter<ThemeChangedEvent>();
  private readonly themes: Map<string, Theme> = new Map();
  private currentId: string;

  constructor(initialThemes: readonly Theme[], defaultId: string) {
    if (initialThemes.length === 0) {
      throw new Error('ThemeRegistry requires at least one initial theme');
    }
    for (const theme of initialThemes) {
      this.themes.set(theme.id, theme);
    }
    if (!this.themes.has(defaultId)) {
      throw new Error(`Default theme id '${defaultId}' not in initial themes`);
    }
    this.currentId = defaultId;
  }

  /** Currently active theme. Scenes should read once at build time + subscribe
   * to {@link events} for runtime swaps. */
  current(): Theme {
    return this.themes.get(this.currentId)!;
  }

  /** All registered themes — used by theme-switcher UI. */
  list(): readonly Theme[] {
    return Array.from(this.themes.values());
  }

  /** Look up a theme by id. */
  get(id: string): Theme | undefined {
    return this.themes.get(id);
  }

  /** Register a new theme (or replace an existing one of the same id). */
  register(theme: Theme): void {
    this.themes.set(theme.id, theme);
  }

  /** Switch the active theme. Emits {@link ThemeChangedEvent} so listeners
   * can swap visuals. No-op if {@code id} is already current. Throws if
   * {@code id} doesn't exist. */
  setCurrent(id: string): void {
    if (id === this.currentId) return;
    const theme = this.themes.get(id);
    if (!theme) {
      throw new Error(`Theme not registered: ${id}`);
    }
    const before = this.currentId;
    this.currentId = id;
    this.events.emit({ kind: 'theme-changed', before, after: id, theme });
  }

  /** Cycle to the next theme in registration order — handy for keyboard
   * shortcuts during dev. Wraps from the last back to the first. */
  cycle(): void {
    const ids = Array.from(this.themes.keys());
    const idx = ids.indexOf(this.currentId);
    const next = ids[(idx + 1) % ids.length]!;
    this.setCurrent(next);
  }

  dispose(): void {
    this.events.clear();
    this.themes.clear();
  }
}
